import {
  type ApiRequest,
  type ApiResponse,
  type ApiResponseSingle,
  properCallApi
} from '@/api/tenantClient';

export const userTableQueryKey = 'admin.user.table';
export const userIdQueryKey = 'admin.user.id';
export const userLastPasscodeQueryKey = 'admin.user.lastPasscode';

export interface User {
  id: number
  email: string
  firstName: string
  lastName: string
  phone: string
  company: string
  location: string
  clientId: string
  emailCcAddresses: string
  pronoun: string
  gender: string
  bio: string
  jobTitle: string
  companyWebsite: string
  facebook: string
  twitter: string
  linkedin: string
  instagram: string
  wechat: string
  privateProfile: boolean
  avatar: string | null
  profile: string | null
  lastSignInAt: string | null
  createdAt: string
  updatedAt: string
}

export interface UserTableRow {
  id: number
  email: string
  firstName: string
  lastName: string
  phone: string
  company: string
  location: string
  clientId: string
  lastSignInAt: string | null
  createdAt: string
  updatedAt: string
}

export interface UserTableQueryRequest {
  page: number
  size: number
  filters?: any
  sorting?: any
  search?: string
}

export interface UserUpdateRequest {
  id: number
  user: {
    firstName?: string
    lastName?: string
    phone?: string
    company?: string
    location?: string
    clientId?: string
    emailCcAddresses?: string
    pronoun?: string
    gender?: string
    bio?: string
    jobTitle?: string
    companyWebsite?: string
    facebook?: string
    twitter?: string
    linkedin?: string
    instagram?: string
    wechat?: string
    privateProfile?: boolean
    tmpAvatar?: string
    tmpProfile?: string
  }
}

export function loadUsersForTable(createTenantAdminRequest: () => ApiRequest) {
  const request = createTenantAdminRequest();
  return async (
    params: UserTableQueryRequest
  ): Promise<ApiResponse<UserTableRow>> => {
    request.method = 'GET';
    request.endpoint.path = '/api/v1/admin/users/table';
    request.endpoint.query = {
      page: (params?.page ?? 0) + 1,
      filters: JSON.stringify(params.filters ?? []),
      sorting: JSON.stringify(params.sorting ?? []),
      size: params.size ?? 10,
      ...params.search ? { search: params.search } : {}
    };
    return await properCallApi<ApiResponse<UserTableRow>>(request);
  };
}

export function loadUserById(createTenantAdminRequest: () => ApiRequest) {
  const request = createTenantAdminRequest();
  return async ({ id }: { id: number }): Promise<ApiResponseSingle<User>> => {
    request.method = 'GET';
    request.endpoint.path = `/api/v1/admin/users/${id}`;
    return await properCallApi<ApiResponseSingle<User>>(request);
  };
}

export function updateUser(createTenantAdminRequest: () => ApiRequest) {
  const request = createTenantAdminRequest();
  return async ({ id, user }: UserUpdateRequest): Promise<ApiResponseSingle<User>> => {
    request.method = 'PUT';
    request.endpoint.path = `/api/v1/admin/users/${id}`;
    request.payload = { user };
    return await properCallApi<ApiResponseSingle<User>>(request);
  };
}

export interface UserLastPasscode {
  passcode: string | null
  createdAt?: string
  expiresAt?: string
  valid: boolean
  message?: string
}

export function loadUserLastPasscode(createTenantAdminRequest: () => ApiRequest) {
  const request = createTenantAdminRequest();
  return async ({ id }: { id: number }): Promise<ApiResponseSingle<UserLastPasscode>> => {
    request.method = 'GET';
    request.endpoint.path = `/api/v1/admin/users/${id}/last_passcode`;
    return await properCallApi<ApiResponseSingle<UserLastPasscode>>(request);
  };
}

export function requestNewPasscode(createTenantAdminRequest: () => ApiRequest) {
  const request = createTenantAdminRequest();
  return async ({ id, eventId }: { id: number; eventId?: number }): Promise<ApiResponseSingle<UserLastPasscode>> => {
    request.method = 'POST';
    request.endpoint.path = `/api/v1/admin/users/${id}/request_passcode`;
    if (eventId) {
      request.payload = { event_id: eventId };
    }
    return await properCallApi<ApiResponseSingle<UserLastPasscode>>(request);
  };
}